/**
 * useChecklistInstanceReport - PDF completo da vistoria
 *
 * Situação via RPC `get_checklist_instance_report`, (re)geração via
 * `request_checklist_instance_report` e download pela Edge Function
 * `checklist-report-download` (URL assinada de 5 min).
 *
 * @version 1.1.0
 * RELEASE: 2026-09-27 v1.1.0 - URL assinada diferencia preview interno e download.
 * RELEASE: 2026-09-27 v1.0.0 - Versão inicial.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';

const logger = createLogger('useChecklistInstanceReport');
const rpc = (name: string, args: Record<string, unknown>) => (supabase.rpc as any)(name, args);

export interface ChecklistInstanceReport {
  id: string;
  version: number;
  status: 'queued' | 'processing' | 'ready' | 'failed';
  sha256: string | null;
  size_bytes: number | null;
}

export function useChecklistInstanceReport(instanceId: string | null, enabled = true) {
  return useQuery({
    queryKey: ['checklist-instance-report', instanceId],
    enabled: !!instanceId && enabled,
    // Enquanto gera, consulta a cada 5 s; pronto/falha encerra
    refetchInterval: (q) => (['queued', 'processing'].includes((q.state.data as ChecklistInstanceReport | null)?.status ?? '') ? 5000 : false),
    queryFn: async () => {
      const { data, error } = await rpc('get_checklist_instance_report', { p_instance_id: instanceId, p_tenant_hint: null });
      if (error) throw error;
      return (data ?? null) as ChecklistInstanceReport | null;
    },
  });
}

export function useRequestChecklistInstanceReport(instanceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { error } = await rpc('request_checklist_instance_report', { p_instance_id: instanceId, p_tenant_hint: null });
      if (error) throw error;
    },
    onSuccess: () => { toast.success('Geração do PDF solicitada'); qc.invalidateQueries({ queryKey: ['checklist-instance-report', instanceId] }); },
    onError: (e: Error) => toast.error(e.message || 'Não foi possível solicitar o PDF'),
  });
}

export type ChecklistReportAccessMode = 'preview' | 'download';

/** Obtém a URL protegida no modo explicitamente permitido pelo servidor. */
export async function getChecklistInstanceReportUrl(
  instanceId: string,
  mode: ChecklistReportAccessMode,
) {
  const { data, error } = await supabase.functions.invoke('checklist-report-download', {
    body: { instance_id: instanceId, mode },
  });
  if (error || !data?.url) {
    logger.warn('Falha ao obter PDF da vistoria', { mode, error: error?.message });
    toast.error('PDF ainda não disponível');
    return null;
  }
  return String(data.url);
}

/** Abre o PDF com cabeçalho de anexo para preservar o download atual. */
export async function downloadChecklistInstanceReport(instanceId: string) {
  const url = await getChecklistInstanceReportUrl(instanceId, 'download');
  if (url) window.open(url, '_blank', 'noopener,noreferrer');
}
